import styled from 'styled-components';
import { designTokens } from '../foundation/DesignTokens';

// Banner container with gradient background - Fixed height for consistency
export const Banner = styled.div`
  padding: 40px 48px;
  min-height: 200px;
  border-radius: ${designTokens.radius.lg};
  color: white;
  margin-bottom: ${designTokens.spacing['2xl']};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;
  box-shadow: ${designTokens.shadows.sm};
  background: ${(props) => props.gradient || 'linear-gradient(135deg, #2C5F4E 0%, #164032 100%)'};

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
    padding: ${designTokens.spacing.xl};
    margin-bottom: ${designTokens.spacing.xl};
    min-height: auto;
  }

  @media (max-width: 600px) {
    padding: ${designTokens.spacing.lg};
  }
`;

// Content area for banner text
export const BannerContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 65%;

  @media (max-width: 900px) {
    max-width: 100%;
    margin-bottom: ${designTokens.spacing.lg};
  }
`;

// Banner title
export const BannerTitle = styled.h1`
  font-family: ${designTokens.fonts.heading};
  font-weight: 800;
  font-size: 1.75rem;
  margin: 0 0 ${designTokens.spacing.sm} 0;
  color: white;
  line-height: 1.2;

  @media (max-width: 900px) {
    font-size: 1.5rem;
  }

  @media (max-width: 600px) {
    font-size: 1.3rem;
  }
`;

// Banner description text
export const BannerDescription = styled.p`
  font-size: 0.95rem;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);

  @media (max-width: 900px) {
    font-size: 0.9rem;
  }

  @media (max-width: 600px) {
    font-size: 0.85rem;
  }
`;

// Decorative SVG background
export const BannerSVG = styled.svg`
  position: absolute;
  right: -20px;
  bottom: -30px;
  width: 200px;
  height: 200px;
  opacity: 0.15;
  transform: rotate(-10deg);
  pointer-events: none;
  fill: currentColor;

  @media (max-width: 900px) {
    width: 150px;
    height: 150px;
    right: -10px;
    bottom: -20px;
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

// Button styled for banner (white background)
export const BannerButton = styled.button`
  background: white;
  color: ${designTokens.colors.gray[900]};
  border: none;
  padding: 10px 20px;
  border-radius: ${designTokens.radius.full};
  font-family: ${designTokens.fonts.heading};
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${designTokens.spacing.sm};
  margin-top: ${designTokens.spacing.lg};

  &:hover {
    background: ${designTokens.colors.gray[50]};
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 900px) {
    font-size: 0.85rem;
    padding: 9px 18px;
    margin-top: ${designTokens.spacing.md};
  }
`;

// Character image for banner
export const BannerImage = styled.img`
  position: absolute;
  right: 20px;
  bottom: 0;
  height: 180px;
  width: auto;
  object-fit: contain;
  pointer-events: none;
  z-index: 1;

  @media (max-width: 900px) {
    height: 140px;
    right: 10px;
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

// Predefined gradient variants - Design System V4.0
export const gradients = {
  // Primary gradients (✅✅ priority)
  purple: 'linear-gradient(175deg, #8410ffd1 0%, #954cffab 100%)',
  indigo: 'linear-gradient(175deg, #2f10ffd1 0%, #4c6effab 100%)',
  magenta: 'linear-gradient(175deg, #c310ffd1 0%, #e14cffab 100%)',
  rose: 'linear-gradient(175deg, #ff1071d1 0%, #ff4c4cab 100%)',
  orange: 'linear-gradient(175deg, #ff8a10d1 0%, #ffcf4cab 100%)',
  green: 'linear-gradient(175deg, #42af13d1 0%, #0fff53b3 100%)',
  violet: 'linear-gradient(175deg, #bd0bd7ab 0%, #3908f5ab 100%)',
  teal: 'linear-gradient(175deg, #0bd7b6a3 0%, #2908f5ad 100%)',
  gray: 'linear-gradient(175deg, #314542a3 0%, #cec9e9ad 100%)',
  dark: 'linear-gradient(175deg, #000000ba 0%, #745bffad 100%)',
  // Secondary gradients (✅ priority)
  blue: 'linear-gradient(135deg, #0460ff8c 0%, #50c1ff 100%)',
  // Additional gradients
  tealClassic: 'linear-gradient(135deg, #009688 0%, #0bcab3 100%)',
  coral: 'linear-gradient(135deg, #ff4d007d 0%, #f8652ad4 100%)',
  // Legacy aliases for backwards compatibility
  pink: 'linear-gradient(175deg, #c310ffd1 0%, #e14cffab 100%)',
  cyan: 'linear-gradient(175deg, #0bd7b6a3 0%, #2908f5ad 100%)',
  darkTeal: 'linear-gradient(175deg, #000000ba 0%, #745bffad 100%)',
};

export default Banner;
